import { Question } from "./Question";
import { ReelNarrationCue, ReelSegment, SlideContent } from "../components/screens/plan/dayplan/components/dailyStep/types";
import { ReelVoiceAuthorReference } from "./ReelVoiceAuthorReference";

export type LearningStyle =
  | "NARRATIVE"
  | "CAUSAL"
  | "SOCRATIC"
  | "MODEL"
  | "ACTION";

export type ArtifactKind =
  | "diagnostic"
  | "checklist"
  | "decision_matrix";

export type ArtifactPromptConfig = {
  enabled: boolean;
  recommendedKind?: ArtifactKind;
  allowedKinds?: ArtifactKind[];
  sourceQuestionIds?: string[];
  ctaLabel?: string;
};

// Link button rendered under the step text in the app. Max 3 per step,
// label <= 60 chars, url must start with https://. Taps log step_cta_press.
export type StepCta = {
  id: string;
  label: string;
  url: string;
};

export type DailyStep = {
  id: string;
  title: string;
  subtitle: string;
  sectionTitle: string;
  description: string;
  text?: string;
  quote: string;
  backgroundImageURL: string;
  reference: string;
  xpReward?: number;
  isCompleted?: boolean;
  type: DAILY_STEP_TYPE;
  dayPlanID: string;
  position?: number;
  planID: string;
  authorID: string;
  authorName: string;
  communityId: string;
  videoURL?: string;
  questions?: Question[];
  actionItems?: string;
  workoutActionId?: string;
  images?: ImageResource[];
  videos?: VideoResource[];
  lists?: StepList[];
  callouts?: StepCallout[];
  slides?: SlideContent[];
  learningStyle?: LearningStyle;
  artifactPrompt?: ArtifactPromptConfig;
  contentVersion?: number;
  experiment?: LessonExperiment;
  variants?: Record<string, StepVariant>;
  ctas?: StepCta[];

  slideAuthorReference?: {
    id: string;
    name: string;
    profilePhotoURL: string;
  } | null;
  reelVoiceAuthorReference?: ReelVoiceAuthorReference | null;
  reelVoiceAudioUrl?: string;
  reelVoiceTimeline?: ReelNarrationCue[];
  reelSegments?: ReelSegment[];
};

export type LessonMutation =
  | "title_or_opening_hook"
  | "pacing"
  | "content_length"
  | "learning_style"
  | "block_order"
  | "video_placement"
  | "callout_or_example"
  | "reflection_prompt"
  | "question_format"
  | "completion_cta"
  | "next_step_preview";

export type StepVariant = Partial<DailyStep> & {
  experimentId?: string;
  mutation?: LessonMutation;
  contentVersion?: number;
};

export type LessonExperiment = {
  version: 1;
  id: string;
  enabled?: boolean;
  mutation: LessonMutation;
  hypothesis: string;
  startedAt?: string | null;
  variants: Array<{
    key: string;
    weight?: number;
    variantKey?: string;
  }>;
};

export enum DAILY_STEP_TYPE {
  DEFAULT = "DEFAULT",
  WORKOUT = "WORKOUT",
  MEDITATION = "MEDITATION",
  DIET = "DIET",
  LEARNING = "LEARNING",
  SLEEP = "SLEEP",
}
export type ImageResource = {
  id: string; // stable id (uuid or index string)
  url: string; // Firebase Storage download URL
  alt?: string;
  caption?: string;
};

export type VideoResource = {
  id: string; // stable id (uuid or index string)
  url: string; // Firebase Storage download URL
  poster?: string; // optional thumbnail/poster image URL
  alt?: string;
  caption?: string;
};

export type StepList = {
  id: string;
  title?: string;
  ordered?: boolean;
  items: string[];
};

export type StepCallout = {
  id: string;
  tone?: "info" | "warning" | "tip" | "note";
  title?: string;
  text: string;
};
